import { useEffect, useState } from 'react';

import { DateRange } from 'react-day-picker';
import { useParams } from 'react-router-dom';

import type { FormikProps, ScheduleTimeFrame } from 'common/contracts';
import { EIntervalsRepeatRequired, EScheduleTabs } from 'constants/enums';

import IntervalOffer from 'components/Schedule/IntervalOffer';
import PermanentOffer from 'components/Schedule/PermanentOffer';
import ScheduledOffer from 'components/Schedule/ScheduledOffer';
import SwitchSelector from 'components/SwitchSelector/SwitchSelector';

import RichText from '../../../components/RichText/RichText';

interface ScheduleTabProps {
  formikProps: FormikProps;
  intervalCron: string;
  setIntervalCron: (intervalCron: string) => void;
}

const ScheduleTab: React.FC<ScheduleTabProps> = ({
  formikProps,
  intervalCron,
  setIntervalCron
}) => {
  const { setFieldValue, values } = formikProps;

  const [repeat, setRepeat] = useState(EIntervalsRepeatRequired.DAILY);
  const [weekDay, setWeekDay] = useState<number>(0);
  const [dayInMonth, setDayInMonth] = useState<number>(1);
  const [appearancesTableData, setAppearancesTableData] = useState<
    ScheduleTimeFrame[]
  >([]);
  const [tabTitle, setTabTitle] = useState(
    values?.schedule?.permanent
      ? values?.schedule?.interval
        ? EScheduleTabs.INTERVALS
        : EScheduleTabs.PERMANENT
      : EScheduleTabs.SCHEDULE
  );
  const { publisherId } = useParams();

  useEffect(() => {
    const timeFramesData: ScheduleTimeFrame[] =
      values.schedule?.timeFrames?.map(
        (date: ScheduleTimeFrame, index: number) => ({
          id: date.id || `id-${index}`,
          startTime: date.startTime,
          endTime: date.endTime,
          notes: date.notes
        })
      );

    setAppearancesTableData(timeFramesData);
  }, [values?.schedule?.timeFrames]);

  const resetScheduleValues = () => {
    setFieldValue('startHour', '');
    setIntervalCron('');
    setRepeat(EIntervalsRepeatRequired.DAILY);
  };

  const onClickSwitch = (value: string) => {
    setTabTitle(value as EScheduleTabs);
    const isPermanent = value !== EScheduleTabs.SCHEDULE;
    setFieldValue('schedule', {
      timeFrames: values?.schedule.timeFrames,
      permanent: isPermanent,
      ...(value === EScheduleTabs.INTERVALS &&
        intervalCron &&
        values.startHour && { interval: intervalCron })
    });
    resetScheduleValues();
  };

  const switchOptions = [
    { value: EScheduleTabs.PERMANENT },
    { value: EScheduleTabs.INTERVALS },
    { value: EScheduleTabs.SCHEDULE }
  ];

  return (
    <div className="w-[456px] mx-auto">
      <RichText
        title="Scheduled Offer"
        description="Set specific dates & hours to your offer"
        className="mb-6"
      />
      <SwitchSelector
        options={switchOptions}
        onClick={onClickSwitch}
        value={tabTitle}
      />
      {tabTitle === EScheduleTabs.SCHEDULE ? (
        <ScheduledOffer
          formikProps={formikProps}
          intervalCron={intervalCron}
          appearancesTableData={appearancesTableData}
          resetScheduleValues={resetScheduleValues}
        />
      ) : tabTitle === EScheduleTabs.INTERVALS ? (
        <IntervalOffer
          formikProps={formikProps}
          repeat={repeat}
          setRepeat={setRepeat}
          weekDay={weekDay}
          setWeekDay={setWeekDay}
          dayInMonth={dayInMonth}
          setDayInMonth={setDayInMonth}
          publisherId={publisherId}
          intervalCron={intervalCron}
          setIntervalCron={setIntervalCron}
        />
      ) : (
        <PermanentOffer formikProps={formikProps} />
      )}
    </div>
  );
};

export default ScheduleTab;
